import type { useGT } from 'gt-react';

type Translate = ReturnType<typeof useGT>;

type UnignoreResultToast = {
  title: string;
  message: string;
  color: 'success' | 'warning' | 'error';
};

export function buildUnignoreResultToast(
  gt: Translate,
  data: {
    unignored: number;
    failures: Array<{ error?: string }>;
  },
): UnignoreResultToast {
  const failureCount = data.failures.length;
  const count = String(data.unignored);
  if (data.unignored === 0 && failureCount === 0) {
    return {
      title: gt('No Invoices Unignored', {
        context: 'toast title when unignore matches nothing',
      }),
      message: gt('No invoices were eligible to unignore', {
        context:
          'toast when unignore updates no invoices and reports no errors',
      }),
      color: 'warning',
    };
  }
  if (failureCount === 0) {
    return {
      title: gt('Invoices Unignored', {
        context: 'toast title when every selected invoice was unignored',
      }),
      message:
        data.unignored === 1
          ? gt('Unignored {count} invoice', {
              context: 'toast when one invoice was unignored',
              count,
            })
          : gt('Unignored {count} invoices', {
              context: 'toast when more than one invoice was unignored',
              count,
            }),
      color: 'success',
    };
  }
  if (data.unignored === 0) {
    return {
      title: gt('Unignore Failed', {
        context: 'toast title when every invoice failed to unignore',
      }),
      message:
        data.failures[0]?.error ||
        gt('Failed to unignore invoices', {
          context: 'toast when unignore fails and the server sent no error',
        }),
      color: 'error',
    };
  }
  return {
    title: gt('Unignore Partially Complete', {
      context: 'toast title when some invoices unignored and some failed',
    }),
    message: gt('Unignored {count} invoices. {failed} failed.', {
      context:
        'toast when unignore succeeds for some invoices and fails for others',
      count,
      failed: String(failureCount),
    }),
    color: 'warning',
  };
}
